import React, { useState, useMemo } from 'react';
import { I18n } from 'react-redux-i18n';
import { Tooltip } from 'antd';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import map from 'lodash/map';
import join from 'lodash/join';

import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import Cell from 'components/Cell';
import ExportButton from 'components/Export';
import Icon from 'components/Icon';
import Pills from 'components/Pills';
import RadioSwitch from 'components/RadioSwitch';
import Spinner from 'components/Spinner';
import Table from 'components/Table';
import Header from 'components/Table/Header';
import ChartInputsBloc from '../components/ChartInputsBloc';

import { useDistributionRaw } from 'apiTyped/hooks/distribution.query';
import { formatParam } from 'utils/formatParam';
import { useUserMe } from 'apiTyped/hooks/users.query';

const Container = styled.div`
  .columnTextOverflow {
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding-left: 12px;
    padding-right: 12px;
  }
`;

const HeaderRightComponents = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;

  .rightComponents {
    margin-left: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .rightComponents > *:first-child {
    margin-right: 20px;
  }

  .linkContainer {
    display: flex;
    text-decoration: none;
  }
  .prepareMyTrip {
    display: inline-flex;
    align-items: center;
    margin-left: 5px;
    text-decoration: none;
    p {
      display: none;
    }

    :hover {
      p {
        display: block;
      }
    }
  }

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    margin-top: 10px;
    margin-left: 0;
  }

  align-items: center;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};

  .labelCheckbox {
    margin-left: 9px;
    margin-right: 15px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 12px;
    color: ${(props) => props.theme.colors['dark-two']};
  }

  .localisationIcon {
    margin-left: 10px;
  }

  .exportButtonContainer {
    padding-bottom: 10px;
    margin-top: 10px;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: rgba(39, 53, 68, 0.03);
  border-radius: 2px;
  padding: 0 10px;
  min-width: 200px;

  .searchInput {
    background: none;
    box-sizing: border-box;
    height: 36px;
    border: 0;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 12px;
    transition: all 100ms ease 0s;
    width: 100%;
    text-overflow: ellipsis;

    &:focus {
      outline: none;
    }
  }
`;

const ToggleLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  font-size: 12px;
  color: ${(props) => props.theme.colors['dark-two']};
  white-space: nowrap;
`;

const ConsumptionFormatFilter = Object.freeze({
  ALL: 'all',
  COCKTAIL: 'cocktail',
  GLASS: 'glass',
  BOTTLE: 'bottle',
  LARGE_FORMAT: 'largeFormat'
});

const RawData = ({ form, isBranded, wineType }) => {
  const [state, setState] = useState({
    data: [],
    sorted: [
      {
        id: 'store',
        desc: false
      }
    ],
    consFormat: ConsumptionFormatFilter.ALL,
    vintageStart: null,
    vintageEnd: null,
    expanded: {},
    showCompetitors: false,
    search: ''
  });
  const campaign = form?.currentCampaign;
  const isSpirit = form?.wineType == 'spirit';

  if (!isSpirit && state.consFormat == ConsumptionFormatFilter.COCKTAIL) {
    // Only spirits can be filtered on "cocktail" format
    setState((prev) => ({ ...prev, consFormat: ConsumptionFormatFilter.ALL }));
  }

  const { user } = useUserMe();

  const shouldDisplayCompetitors = user.client.id == 602;

  // Memoize the query options to prevent unnecessary re-fetches
  const queryOptions = useMemo(() => {
    if (!form?.wine?.length) return {};

    return isBranded
      ? {
          brands: form.wine.map((item) => item.label),
          wine_type: form.wineType,
          competitors: state.showCompetitors || undefined,
          rivals:
            state.showCompetitors && form.competitors?.length
              ? form.competitors
              : undefined,
          search: state.search || undefined
        }
      : {
          wines: form.wine.map((item) => item.label),
          wine_type: form.wineType,
          competitors: state.showCompetitors || undefined,
          rivals:
            state.showCompetitors && form.competitors?.length
              ? form.competitors
              : undefined,
          search: state.search || undefined
        };
  }, [
    form?.wine,
    isBranded,
    form?.competitors,
    form?.wineType,
    state.showCompetitors,
    state.search
  ]);

  // Use the new hook to fetch raw data
  const { isLoading, data } = useDistributionRaw(
    form?.currentCampaign?.id,
    queryOptions,
    !!(form?.currentCampaign?.id && form?.wine?.length)
  );

  const {
    sorted,
    consFormat,
    vintageStart,
    vintageEnd,
    expanded,
    showCompetitors,
    search
  } = state;

  const getDisplayedFormatValue = (value) => {
    if (value) {
      if (!isNaN(parseInt(value))) {
        return (
          value.toLocaleString('fr-FR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }) + ' ml'
        );
      }
      if (value && value === ConsumptionFormatFilter.GLASS) {
        return I18n.t('restaurants&caves.raw.glass');
      }
      return value;
    }
    return '-';
  };

  const handleSort = (id) => {
    let newSorted = sorted;
    let desc = true;
    if (newSorted.length > 0 && newSorted[0].id === id) {
      desc = !newSorted[0].desc;
    }
    newSorted = [{ id, desc }];
    setState((prev) => ({ ...prev, sorted: newSorted, expanded: {} }));
  };

  let consFormatOptions = [
    { label: I18n.t('placeholderAll'), value: ConsumptionFormatFilter.ALL },
    {
      label: I18n.t(`restaurants&caves.raw.${ConsumptionFormatFilter.BOTTLE}`),
      value: ConsumptionFormatFilter.BOTTLE
    },
    {
      label: I18n.t(
        `restaurants&caves.raw.${ConsumptionFormatFilter.LARGE_FORMAT}`
      ),
      value: ConsumptionFormatFilter.LARGE_FORMAT
    },
    {
      label: I18n.t(`restaurants&caves.raw.${ConsumptionFormatFilter.GLASS}`),
      value: ConsumptionFormatFilter.GLASS
    }
  ];
  if (isSpirit) {
    consFormatOptions.push({
      label: I18n.t(
        `restaurants&caves.raw.${ConsumptionFormatFilter.COCKTAIL}`
      ),
      value: ConsumptionFormatFilter.COCKTAIL
    });
  }

  const getColumns = () => {
    return [
      {
        accessor: 'date',
        Header: () => (
          <Header
            title={I18n.t('restaurants&caves.raw.columns.date')}
            sortable={true}
            sort={() => handleSort('date')}
          />
        ),
        Cell: (props) => <Cell {...props} value={props.value} />,
        sortMethod: (a, b) => Number(a ? a : 0) - Number(b ? b : 0),
        width: 100
      },
      {
        accessor: '',
        Header: () => (
          <Header
            title={I18n.t('restaurants&caves.raw.columns.market')}
            styles={`justify-content:flex-start;padding-left:15px;padding-right:15px;`}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            className="columnTextOverflow"
            value={
              <Tooltip
                title={get(form, 'currentCampaign.market.name', '')}
                placement="bottomLeft"
              >
                {get(form, 'currentCampaign.market.name', '')}
              </Tooltip>
            }
          />
        )
      },
      {
        accessor: 'store',
        Header: () => (
          <Header
            title={I18n.t('restaurants&caves.raw.columns.pos')}
            sortable={true}
            sort={() => handleSort('store')}
            styles={`justify-content:flex-start;padding-left:15px;padding-right:15px;`}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            className="columnTextOverflow"
            styles={`font-weight: 600;`}
            value={
              <Tooltip placement="bottomLeft" title={props.value}>
                {props.value}
              </Tooltip>
            }
          />
        )
      },
      {
        accessor: 'wine_name',
        Header: () => (
          <Header
            title={I18n.t('restaurants&caves.raw.columns.wine')}
            sortable={true}
            sort={() => handleSort('wine_name')}
            styles={`justify-content:flex-start;padding-left:15px;padding-right:15px;`}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            className="columnTextOverflow"
            value={
              <Tooltip placement="bottomLeft" title={props.value}>
                {props.value}
              </Tooltip>
            }
          />
        )
      },
      {
        accessor: 'vintage',
        Header: () => (
          <Header
            title={I18n.t('restaurants&caves.raw.columns.vintage')}
            sortable={true}
            sort={() => handleSort('vintage')}
          />
        ),
        Cell: (props) => <Cell {...props} value={props.value} />,
        width: 100
      },
      {
        accessor: 'format',
        Header: () => (
          <Header
            title={I18n.t('restaurants&caves.raw.columns.format')}
            sortable={true}
            sort={() => handleSort('format')}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            value={getDisplayedFormatValue(props.value)}
            styles={`border-right: 1px solid #e8e9eb`}
          />
        ),
        width: 150
      },
      {
        accessor: 'local_price',
        Header: () => (
          <Header
            title={I18n.t('restaurants&caves.raw.columns.localPrice')}
            sortable={true}
            sort={() => handleSort('local_price')}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            value={
              props.value
                ? props.value.toLocaleString('fr-FR', {
                    style: 'currency',
                    currency: get(props, 'row._original.local_currency')
                      ? get(props, 'row._original.local_currency')
                      : 'EUR',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                  })
                : '-'
            }
          ></Cell>
        ),
        sortMethod: (a, b) => Number(a) - Number(b)
      },
      {
        accessor: 'price',
        Header: () => (
          <Header
            title={I18n.t('restaurants&caves.raw.columns.price')}
            sortable={true}
            sort={() => handleSort('price')}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            value={
              props.value
                ? props.value.toLocaleString('fr-FR', {
                    style: 'currency',
                    currency: get(props, 'row._original.displayed_currency')
                      ? get(props, 'row._original.displayed_currency')
                      : 'EUR',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                  })
                : null
            }
          ></Cell>
        ),
        sortMethod: (a, b) => Number(a) - Number(b)
      },
      {
        accessor: 'evolution',
        Header: () => (
          <Header
            title={I18n.t('restaurants&caves.raw.columns.evol')}
            sortable={true}
            sort={() => handleSort('evol')}
          />
        ),
        Cell: (props) => <Cell {...props} value={props.value} />,
        width: 180
      }
    ];
  };

  const formatData = () => {
    const current = data?.current || [];
    let last_year = data?.last_year || [];
    last_year = last_year.map((item) => ({ ...item, lastYear: true }));
    let rawData = current.concat(last_year);

    if (consFormat !== ConsumptionFormatFilter.ALL) {
      rawData = rawData.filter((item) => item.format_group === consFormat);
    }
    rawData = rawData.filter(
      (item) =>
        (!vintageStart ||
          !vintageStart.value ||
          item.vintage >= vintageStart.value) &&
        (!vintageEnd || !vintageEnd.value || item.vintage <= vintageEnd.value)
    );

    return rawData.map((item) => {
      return {
        ...item,
        wine: item.wine && item.wine_name ? item.wine_name : '-'
      };
    });
  };

  const getVintageOptions = () => {
    const current = data?.current || [];
    let last_year = data?.last_year || [];
    let rawData = current.concat(last_year);
    if (rawData && rawData.length > 0) {
      return [...new Set(rawData.map((item) => item.vintage))]
        .sort()
        .reverse()
        .map((item) => ({ value: item, label: item }));
    }
    return [];
  };
  const { wine, competitors } = form;
  const formattedData = formatData();
  const columns = getColumns();
  const vintageOptions = getVintageOptions();
  const options = isBranded
    ? {
        brands: formatParam(JSON.stringify(wine.map((item) => item.label))),
        rivals:
          showCompetitors && competitors?.length
            ? formatParam(JSON.stringify(competitors))
            : undefined,
        wine_type: form.wineType,
        competitors: showCompetitors || undefined,
        search: search || undefined
      }
    : {
        wines: formatParam(JSON.stringify(wine.map((item) => item.label))),
        rivals:
          showCompetitors && competitors?.length
            ? formatParam(JSON.stringify(competitors))
            : undefined,
        wine_type: form.wineType,
        competitors: showCompetitors || undefined,
        search: search || undefined
      };

  return (
    <React.Fragment>
      <Container>
        {!isEmpty(campaign) && (
          <Card>
            <CardHeader
              title={I18n.t('restaurants&caves.raw.title')}
              fontSize="20px"
              styles={`.cardHeaderRight{width:100%;margin-left:40px;};align-items:center;`}
              rightComponents={
                <HeaderRightComponents>
                  <Pills
                    options={consFormatOptions.map((item) => ({
                      ...item,
                      active: item.value === consFormat
                    }))}
                    onChange={({ value }) =>
                      setState((prev) => ({ ...prev, consFormat: value }))
                    }
                  />

                  {shouldDisplayCompetitors && (
                    <>
                      <ToggleLabel>
                        <RadioSwitch
                          checked={showCompetitors}
                          onChange={(checked) =>
                            setState((prev) => ({
                              ...prev,
                              showCompetitors: checked
                            }))
                          }
                        />
                        {I18n.t('restaurants&caves.raw.displayCompetitors')}
                      </ToggleLabel>
                      <SearchContainer>
                        <input
                          name="rawDataSearch"
                          type="text"
                          value={search}
                          placeholder={I18n.t(
                            'restaurants&caves.raw.searchPlaceholder'
                          )}
                          onChange={(e) =>
                            setState((prev) => ({
                              ...prev,
                              search: e.target.value
                            }))
                          }
                          className="searchInput"
                        />
                        <Icon
                          alt="search"
                          className="magnifyingGlass"
                          icon="magnifyingGlass"
                        />
                      </SearchContainer>
                    </>
                  )}
                  <div className="rightComponents">
                    <ChartInputsBloc
                      noWine
                      vintageEnd={vintageEnd}
                      vintageStart={vintageStart}
                      vintageOptions={[
                        { value: null, label: I18n.t('placeholderAll') },
                        ...vintageOptions
                      ]}
                      onChange={(field, value) =>
                        setState((prev) => ({ ...prev, [field]: value }))
                      }
                    />
                    <ExportButton
                      path={`/distribution/${campaign.id}/raw/xls?no_page`}
                      options={options}
                      name={`${get(form, 'currentCampaign.network.name')}-${get(
                        form,
                        'currentCampaign.market.name'
                      )}-${get(form, 'currentCampaign.year')}-${join(
                        map(get(form, 'wine'), 'label'),
                        '-'
                      )}-${I18n.t('restaurants&caves.raw.title')}`}
                      xls
                      newApi
                    />
                  </div>
                </HeaderRightComponents>
              }
            />
            <Table
              columns={columns}
              data={formattedData}
              sorted={sorted}
              expanded={expanded}
            />
          </Card>
        )}
        <Spinner loading={isLoading} />
      </Container>
    </React.Fragment>
  );
};

export default RawData;
